import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { sendNotification } from '@/utils/sendNotification';
import { ContactSubmission } from './useContactSubmissions';

export const useContactNotifications = () => {
  // Send auto-response email to customer
  const sendAutoResponse = async (submission: ContactSubmission) => {
    try {
      const templateData = {
        name: submission.name,
        subject: submission.subject,
        category_name: submission.contact_categories?.name || 'General',
        priority: submission.priority,
        auto_response_template: submission.contact_categories?.auto_response_template || null
      };

      await sendNotification(
        'contactAutoResponse',
        submission.email,
        templateData
      );

      console.log('Auto-response email sent successfully');
    } catch (error) {
      console.error('Failed to send auto-response email:', error);
    }
  };

  // Send admin notification email
  const sendAdminNotification = async (submission: ContactSubmission) => {
    try {
      // Get admin emails to notify
      const { data: admins } = await supabase
        .from('profiles')
        .select('email, name')
        .in('role', ['admin', 'super_admin']);

      if (!admins || admins.length === 0) return;

      const templateData = {
        name: submission.name,
        email: submission.email,
        phone: submission.phone,
        subject: submission.subject,
        message: submission.message,
        category_name: submission.contact_categories?.name || 'General',
        priority: submission.priority,
        priority_urgent: submission.priority === 'urgent',
        priority_high: submission.priority === 'high',
        priority_medium: submission.priority === 'medium',
        priority_low: submission.priority === 'low'
      };

      // Send to all admins
      for (const admin of admins) {
        await sendNotification(
          'contactAdminNotification',
          admin.email,
          templateData
        );
      }

      console.log(`Admin notification emails sent to ${admins.length} admins`);
    } catch (error) {
      console.error('Failed to send admin notification emails:', error);
    }
  };

  // Send status update email to customer
  const sendStatusUpdate = async (
    submission: ContactSubmission, 
    responseMessage?: string,
    assignedToName?: string
  ) => {
    try {
      const templateData = {
        name: submission.name,
        subject: submission.subject,
        status: submission.status,
        status_resolved: submission.status === 'resolved',
        response_message: responseMessage,
        assigned_to_name: assignedToName
      };

      await sendNotification(
        'contactStatusUpdate',
        submission.email,
        templateData
      );

      console.log('Status update email sent successfully');
    } catch (error) {
      console.error('Failed to send status update email:', error);
    }
  };

  // Set up real-time notifications for high priority submissions
  const setupRealtimeNotifications = () => {
    const channel = supabase
      .channel('contact-submissions')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'contact_submissions'
        },
        async (payload) => {
          const submission = payload.new as ContactSubmission;
          
          // Send auto-response
          await sendAutoResponse(submission);
          
          // Send admin notification for high priority items
          if (['high', 'urgent'].includes(submission.priority)) {
            await sendAdminNotification(submission);
          }
          
          // Create in-app notification for admins
          const { data: admins } = await supabase
            .from('profiles')
            .select('id')
            .in('role', ['admin', 'super_admin']);
          
          if (admins) {
            for (const admin of admins) {
              await supabase
                .from('notifications')
                .insert([{
                  user_id: admin.id,
                  type: 'contact_new_submission',
                  title: `New ${submission.priority} priority contact`,
                  body: `${submission.name} submitted: ${submission.subject}`,
                  link: '/admin/contact'
                }]);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  return {
    sendAutoResponse,
    sendAdminNotification,
    sendStatusUpdate,
    setupRealtimeNotifications
  };
};